/**
 * RichTextEditor Component
 *
 * A rich text editor built with Tiptap for creating updates, comments, and replies.
 * Supports formatting (bold, italic, underline, links), mentions, and attachments.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { EditorToolbar } from './EditorToolbar';
import { createMentionExtension, MentionExtension } from './mention-config';
import type { User } from '@wmp/shared/types';

export interface RichTextEditorProps {
  /** Initial content as HTML */
  content?: string;
  /** Placeholder text when editor is empty */
  placeholder?: string;
  /** Whether the editor is editable */
  editable?: boolean;
  /** Minimum height of the editor */
  minHeight?: string;
  /** Maximum height of the editor (will scroll) */
  maxHeight?: string;
  /** Whether to show the toolbar */
  showToolbar?: boolean;
  /** Whether to autofocus on mount */
  autoFocus?: boolean;
  /** Callback when content changes */
  onChange?: (html: string, text: string) => void;
  /** Callback when editor is focused */
  onFocus?: () => void;
  /** Callback when editor is blurred */
  onBlur?: () => void;
  /** Callback to fetch mention suggestions */
  onMentionSearch?: (query: string) => Promise<User[]> | User[];
  /** Callback when a user is mentioned */
  onMention?: (user: User) => void;
  /** Additional CSS classes */
  className?: string;
}

export interface RichTextEditorRef {
  /** Get current HTML content */
  getHTML: () => string;
  /** Get current plain text content */
  getText: () => string;
  /** Set content programmatically */
  setContent: (content: string) => void;
  /** Clear the editor */
  clear: () => void;
  /** Focus the editor */
  focus: () => void;
  /** Check if editor has content */
  isEmpty: () => boolean;
  /** Get the editor instance */
  getEditor: () => Editor | null;
}

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  function RichTextEditor(
    {
      content = '',
      placeholder = 'Write something...',
      editable = true,
      minHeight = '100px',
      maxHeight,
      showToolbar = true,
      autoFocus = false,
      onChange,
      onFocus,
      onBlur,
      onMentionSearch,
      onMention: _onMention,
      className = '',
    },
    ref
  ) {
    // Create mention extension based on whether search is provided
    const mentionExtension = useMemo(() => {
      if (onMentionSearch) {
        return createMentionExtension(onMentionSearch);
      }
      return MentionExtension;
    }, [onMentionSearch]);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // Disable heading for simpler use in updates/comments
          heading: false,
          // Enable hard break for shift+enter
          hardBreak: {
            keepMarks: true,
          },
        }),
        Placeholder.configure({
          placeholder,
          emptyEditorClass: 'is-editor-empty',
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            class: 'text-primary-600 hover:text-primary-700 underline',
          },
        }),
        Underline,
        Highlight.configure({
          multicolor: false,
          HTMLAttributes: {
            class: 'bg-yellow-200',
          },
        }),
        mentionExtension,
      ],
      content,
      editable,
      autofocus: autoFocus ? 'end' : false,
      onUpdate: ({ editor }) => {
        if (onChange) {
          onChange(editor.getHTML(), editor.getText());
        }
      },
      onFocus: () => {
        onFocus?.();
      },
      onBlur: () => {
        onBlur?.();
      },
      editorProps: {
        attributes: {
          class: `prose prose-sm max-w-none focus:outline-none`,
        },
      },
    });

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getHTML: () => editor?.getHTML() ?? '',
      getText: () => editor?.getText() ?? '',
      setContent: (content: string) => {
        editor?.commands.setContent(content);
      },
      clear: () => {
        editor?.commands.clearContent();
      },
      focus: () => {
        editor?.commands.focus();
      },
      isEmpty: () => editor?.isEmpty ?? true,
      getEditor: () => editor,
    }));

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // Allow shift+enter for new lines, enter submits if in a form context
        if (e.key === 'Enter' && !e.shiftKey && e.metaKey) {
          // Cmd/Ctrl+Enter can be used to submit
          e.preventDefault();
        }
      },
      []
    );

    if (!editor) {
      return null;
    }

    return (
      <div className={`rich-text-editor ${className}`}>
        {showToolbar && editable && (
          <EditorToolbar editor={editor} />
        )}
        <div
          className={`editor-content-wrapper ${editable ? 'cursor-text' : ''}`}
          style={{
            minHeight,
            maxHeight,
            overflowY: maxHeight ? 'auto' : undefined,
          }}
          onKeyDown={handleKeyDown}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);

// CSS for the editor placeholder
const editorStyles = `
.ProseMirror p.is-editor-empty:first-child::before {
  color: #9ca3af;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.ProseMirror:focus {
  outline: none;
}

.ProseMirror {
  padding: 0.75rem;
}

.ProseMirror p {
  margin: 0;
}

.ProseMirror p + p {
  margin-top: 0.5rem;
}

/* Mention styling */
.mention {
  background-color: #dbeafe;
  border-radius: 0.25rem;
  padding: 0.125rem 0.25rem;
  color: #1d4ed8;
  font-weight: 500;
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined') {
  const styleId = 'rich-text-editor-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = editorStyles;
    document.head.appendChild(style);
  }
}
